import React from "react";
import { Locale } from "@linkfluence/models/locale";
import { getAgeLabelNode } from "@radarly/components/clause/labels/label/age";
import { getFollowersLabelNode } from "@radarly/components/clause/labels/label/followers";
import {
  Label,
  formatTermClauseLabel,
} from "@radarly/components/clause/labels/label/index";
import { queryLabel } from "@radarly/components/clause/labels/label/query";
import { getReviewLabelNode } from "@radarly/components/clause/labels/label/reviews";
import {
  AgeClauseLabel,
  ClauseLabel,
  FollowersClauseLabel,
  ReviewClauseLabel,
  TermClauseLabel,
  doesRangeClauseLabelHasLowerValue,
  doesRangeClauseLabelHasLowerbound,
  doesRangeClauseLabelHasUpperValue,
  doesRangeClauseLabelHasUpperbound,
  isAgeClauseLabel,
  isFollowersClauseLabel,
  isQueryClauseLabel,
  isRangeClauseLabelLowerValueZero,
  isRangeClauseLabelRangeEqual,
  isRangeClauseLabelRangeZero,
  isRangeClauseLabelUpperValueZero,
  isReviewClauseLabel,
} from "@radarly/models/clause/label";

type ClauseLabelProps = {
  label: ClauseLabel;
  locale: Locale;
  colorized?: boolean;
};

export default function RadarlyComponentsClauseLabel({
  label,
  locale,
  colorized,
}: ClauseLabelProps) {
  if (isQueryClauseLabel(label)) return queryLabel(label, label.excluded);
  return (
    <Label excluded={label.excluded} colorized={colorized}>
      {getClauseLabel(label, locale)}
    </Label>
  );
}

export function getClauseLabel(label: ClauseLabel, locale: Locale) {
  if (isReviewClauseLabel(label)) {
    return getReviewClauseLabel(label, locale);
  }
  if (isAgeClauseLabel(label)) {
    return getAgeClauseLabel(label, locale);
  }
  if (isFollowersClauseLabel(label)) {
    return getFollowersClauseLabel(label, locale);
  }
  return formatTermClauseLabel(label as TermClauseLabel);
}

function getReviewClauseLabel(label: ReviewClauseLabel, locale: Locale) {
  if (
    doesRangeClauseLabelHasLowerbound(label) &&
    !doesRangeClauseLabelHasUpperbound(label)
  ) {
    return getReviewLabelNode("to", label, locale);
  }
  if (
    !doesRangeClauseLabelHasLowerbound(label) &&
    doesRangeClauseLabelHasUpperbound(label)
  ) {
    return getReviewLabelNode("from", label, locale);
  }
  if (isRangeClauseLabelRangeEqual(label)) {
    return getReviewLabelNode("equal", label);
  }
  return getReviewLabelNode("default", label);
}

function getAgeClauseLabel(label: AgeClauseLabel, locale: Locale) {
  if (
    (doesRangeClauseLabelHasLowerbound(label) &&
      doesRangeClauseLabelHasUpperbound(label)) ||
    isRangeClauseLabelRangeZero(label)
  ) {
    return getAgeLabelNode("minmax", label, locale);
  }
  if (
    doesRangeClauseLabelHasUpperbound(label) ||
    isRangeClauseLabelLowerValueZero(label)
  ) {
    return getAgeLabelNode("from", label, locale);
  }
  if (
    doesRangeClauseLabelHasLowerbound(label) ||
    isRangeClauseLabelUpperValueZero(label)
  ) {
    return getAgeLabelNode("to", label, locale);
  }
  return getAgeLabelNode("default", label, locale);
}

function getFollowersClauseLabel(label: FollowersClauseLabel, locale: Locale) {
  if (
    !doesRangeClauseLabelHasLowerValue(label) &&
    !doesRangeClauseLabelHasUpperValue(label)
  ) {
    return getFollowersLabelNode("no-range", label, locale);
  }
  if (
    doesRangeClauseLabelHasLowerValue(label) &&
    !doesRangeClauseLabelHasUpperValue(label)
  ) {
    return getFollowersLabelNode("from", label, locale);
  }
  if (
    !doesRangeClauseLabelHasLowerValue(label) &&
    doesRangeClauseLabelHasUpperValue(label)
  ) {
    return getFollowersLabelNode("to", label, locale);
  }
  return getFollowersLabelNode("default", label, locale);
}
